import React, { useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useToast } from 'app/components/ui/use-toast';
import { useProductsSlice } from 'app/pages/Product/slice';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Checkbox } from 'app/components/ui/checkbox';
import { Type, Save, X } from 'lucide-react';

interface AttributeFormInputs {
  name: string;
  slug: string;
  product_types: string[];
  display_order: number;
  description?: string;
  id?: string;
}

// Product types
const PRODUCT_TYPES = [
  { value: '1', label: 'Digital Product' },
  { value: '2', label: 'Physical Product' },
];

const AddEditForm: React.FC<{
  selectedAttribute: AttributeFormInputs | null;
  onFormSubmit: (data: AttributeFormInputs) => void;
}> = ({ selectedAttribute, onFormSubmit }) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<AttributeFormInputs>();
  const {
    useAddProductsAttributeTypeMutation,
    useUpdateProductsAttributeTypeMutation,
    useLazyGetProductsAttributeTypeQuery,
  } = useProductsSlice();

  const [addAttributeType, { isLoading: isAdding }] =
    useAddProductsAttributeTypeMutation();
  const [updateAttributeType, { isLoading: isUpdating }] =
    useUpdateProductsAttributeTypeMutation();
  const [getAttributeList] = useLazyGetProductsAttributeTypeQuery();

  const { toast } = useToast();

  useEffect(() => {
    if (selectedAttribute) {
      setValue('name', selectedAttribute.name);
      setValue('slug', selectedAttribute.slug);

      // Handle both product_types and applicable_product_types field names
      // Convert numbers to strings for consistency
      const productTypes =
        selectedAttribute.product_types ||
        selectedAttribute.applicable_product_types ||
        [];
      const stringTypes = productTypes.map(type => type.toString());
      setValue('product_types', stringTypes);

      setValue('display_order', selectedAttribute.display_order || 0);
      setValue('description', selectedAttribute.description || '');
    } else {
      // For new attributes, get the next available display order
      const getNextDisplayOrder = async () => {
        try {
          const response = await getAttributeList({}).unwrap();
          const attributes = response?.data || [];

          // Find the highest display order and add 1
          const maxOrder = Math.max(
            ...attributes.map(attr => attr.display_order || 0),
            0,
          );
          const nextOrder = maxOrder + 1;

          reset({
            name: '',
            slug: '',
            product_types: [],
            display_order: nextOrder,
            description: '',
          });
        } catch (error) {
          // If there's an error fetching attributes, default to 1
          reset({
            name: '',
            slug: '',
            product_types: [],
            display_order: 1,
            description: '',
          });
        }
      };

      getNextDisplayOrder();
    }
  }, [selectedAttribute, setValue, reset, getAttributeList]);

  const watchedName = watch('name');
  const watchedProductTypes = watch('product_types');

  // Auto-generate slug from name
  useEffect(() => {
    if (watchedName && !selectedAttribute) {
      const generatedSlug = watchedName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
        .trim();

      setValue('slug', generatedSlug);
    }
  }, [watchedName, setValue, selectedAttribute]);

  // Handle product types checkbox changes
  const handleProductTypeChange = (typeValue: string, checked: boolean) => {
    const currentTypes = watchedProductTypes || [];
    if (checked) {
      // Add the type if it's not already in the array
      if (!currentTypes.includes(typeValue)) {
        setValue('product_types', [...currentTypes, typeValue]);
      }
    } else {
      // Remove the type from the array
      setValue(
        'product_types',
        currentTypes.filter(type => type !== typeValue),
      );
    }
  };

  const onSubmit: SubmitHandler<AttributeFormInputs> = async data => {
    try {
      if (selectedAttribute?.id) {
        await updateAttributeType({
          id: selectedAttribute.id,
          ...data,
        }).unwrap();
        toast({
          title: 'Success',
          description: 'Attribute updated successfully!',
          variant: 'default',
        });
      } else {
        await addAttributeType(data).unwrap();
        toast({
          title: 'Success',
          description: 'Attribute added successfully!',
          variant: 'default',
        });
      }
      reset();
      onFormSubmit(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to process. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleCancel = () => {
    reset();
    onFormSubmit({ name: '', slug: '' });
  };

  return (
    <div className="p-6">
      {/* Form Header */}
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
          <Type className="w-4 h-4 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {selectedAttribute ? 'Edit Attribute' : 'Add New Attribute'}
          </h2>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {selectedAttribute
              ? 'Update attribute information'
              : 'Create a new product attribute'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Name Field */}
        <div className="space-y-2">
          <Label
            htmlFor="name"
            className="text-xs font-medium text-gray-800 dark:text-gray-200 flex items-center"
          >
            <span className="w-1.5 h-1.5 bg-gradient-to-r from-orange-500 to-red-500 rounded-full mr-2"></span>
            Attribute Name <span className="text-red-500 ml-1">*</span>
          </Label>
          <Input
            type="text"
            id="name"
            placeholder="Enter attribute name (e.g., Color, Size)"
            {...register('name', { required: 'Name is required' })}
            className={`h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 rounded-lg transition-all duration-200 text-sm ${errors.name
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : ''
              }`}
          />
          {errors.name && (
            <p className="text-red-500 text-xs font-medium">
              {errors.name.message}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
            Display name for the attribute
          </p>
        </div>

        {/* Slug Field */}
        <div className="space-y-2">
          <Label
            htmlFor="slug"
            className="text-xs font-medium text-gray-800 dark:text-gray-200 flex items-center"
          >
            <span className="w-1.5 h-1.5 bg-gradient-to-r from-red-500 to-pink-500 rounded-full mr-2"></span>
            Attribute Slug <span className="text-red-500 ml-1">*</span>
            {!selectedAttribute && watchedName && (
              <span className="text-xs text-green-600 dark:text-green-400 ml-2 font-normal">
                (Auto-generated)
              </span>
            )}
          </Label>
          <Input
            type="text"
            id="slug"
            placeholder="Enter slug (e.g., color, size)"
            {...register('slug', {
              required: 'Slug is required',
              pattern: {
                value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                message: 'Slug must be alphanumeric and kebab-case',
              },
            })}
            readOnly={!selectedAttribute && watchedName}
            className={`h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-red-500 focus:border-red-500 rounded-lg transition-all duration-200 text-sm ${errors.slug
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : ''
              } ${!selectedAttribute && watchedName
                ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed'
                : ''
              }`}
          />
          {errors.slug && (
            <p className="text-red-500 text-xs font-medium">
              {errors.slug.message}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
            {!selectedAttribute && watchedName
              ? 'Slug is automatically generated from the name'
              : 'Unique identifier for the attribute (lowercase, kebab-case)'}
          </p>
        </div>

        {/* Product Types Field */}
        <div className="space-y-2">
          <Label className="text-xs font-medium text-gray-800 dark:text-gray-200 flex items-center">
            <span className="w-1.5 h-1.5 bg-gradient-to-r from-green-500 to-teal-500 rounded-full mr-2"></span>
            Applicable Product Types
          </Label>
          <div className="space-y-3">
            {PRODUCT_TYPES.map(type => (
              <div key={type.value} className="flex items-center space-x-3">
                <Checkbox
                  id={`product_type_${type.value}`}
                  checked={watchedProductTypes?.includes(type.value) || false}
                  onCheckedChange={checked =>
                    handleProductTypeChange(type.value, checked as boolean)
                  }
                  className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                />
                <Label
                  htmlFor={`product_type_${type.value}`}
                  className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                >
                  {type.label}
                </Label>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
            Select which product types this attribute applies to
          </p>
        </div>

        {/* Display Order Field */}
        <div className="space-y-2">
          <Label
            htmlFor="display_order"
            className="text-xs font-medium text-gray-800 dark:text-gray-200 flex items-center"
          >
            <span className="w-1.5 h-1.5 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full mr-2"></span>
            Display Order
          </Label>
          <Input
            type="number"
            id="display_order"
            placeholder="0"
            {...register('display_order', { valueAsNumber: true })}
            className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 rounded-lg transition-all duration-200 text-sm"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
            Lower numbers appear first in forms
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100 dark:border-gray-700">
          {selectedAttribute && (
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="h-10 px-4 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500 transition-all duration-200"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            disabled={isAdding || isUpdating}
            className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white transition-all duration-200 disabled:opacity-50 h-10 px-6"
          >
            <Save className="w-4 h-4 mr-2" />
            {isAdding || isUpdating
              ? 'Processing...'
              : selectedAttribute
                ? 'Save Changes'
                : 'Add Attribute'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddEditForm;
